import React, { useEffect } from 'react';
import { getAllTiers } from '../../api/events';
import { updateTier, deleteTier } from '../../api/tier';
import { connect } from 'react-redux';
import { Loading, PageHeader } from '../../components';
import DataTable from '../../components/DataTable/DataTable';
import CancelAlertPopup from '../../components/CancelAlertPopup/CancelAlertPopup';
import useComponentState from './../../utils/useComponentState';
import styles from './tiersManagement.module.scss';
import { Drawer } from 'rsuite';
import { toast } from 'react-toastify';
import { DUMMY_TIERS } from '../addTiers/addTiersConstants';
import { usePermissions } from '../../hooks/usePermissions';
import PermissionGate from '../../components/PermissionGate/PermissionGate';
import { TIER_MANAGEMENT_PERMISSIONS } from '../../constants/permissions';

const validateEditForm = (editForm) => {
  const errors = {};

  if (!editForm.name || editForm.name.trim().length === 0) {
    errors.name = 'Tier name is required.';
  }

  const minPts = parseFloat(editForm.minPoints);
  if (editForm.minPoints === '' || isNaN(minPts) || minPts < 1) {
    errors.minPoints = 'Minimum redemption points must be at least 1.';
  }

  const ratio = parseFloat(editForm.redemptionRatio);
  if (editForm.redemptionRatio === '' || isNaN(ratio) || ratio <= 0) {
    errors.redemptionRatio = 'Redemption ratio is required and must be greater than 0.';
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

const TiersManagement = (props) => {
  const { auth: { token } } = props.auth;
  const [state, setState] = useComponentState({
    tableHeading: {
      column: ['Tier Name', 'Description', 'Min. Redemption Points', 'Redemption Ratio'],
    },
    tiersData: [],
    tiersLoadingError: '',
    loading: false,

    showTierDrawer: false,
    drawerData: null,

    editMode: false,
    editForm: {
      name: '',
      minPoints: '',
      redemptionRatio: '',
      description: '',
    },
    fieldErrors: {},
    editSaving: false,

    showDeleteConfirmation: false,
    tierToDelete: null,
  });

  const { tableHeading, tiersData, tiersLoadingError, loading } = state;

  const { can } = usePermissions();

  useEffect(() => {
    getTierDetails();
  }, []);

  const getTierDetails = async () => {
    setState({ loading: true, tiersData: [], tiersLoadingError: '' });
    try {
      const response = await getAllTiers(0, token);
      setState({ tiersData: response || [] });

    } catch (error) {
      setState({ tiersLoadingError: 'Oops, something went wrong. Please try again' });
    }
    setState({ loading: false });
  };

  const handleDrawerOpen = (rowData) => {
    if (!rowData) return;

    setState({
      showTierDrawer: true,
      drawerData: rowData,
      editMode: false,
      editForm: {
        name: rowData.name || 'N/A',
        minPoints: rowData.minPoint || 'N/A',
        redemptionRatio: rowData.redemptionRatio || 'N/A',
        description: rowData.description || 'N/A',
      },
      fieldErrors: {},
    });
  };

  const handleInputChange = (field, value) => {
    setState({
      editForm: { ...state.editForm, [field]: value },
      fieldErrors: { ...state.fieldErrors, [field]: '' },
    });
  };

  const handleSaveTier = async () => {
    if (!can(TIER_MANAGEMENT_PERMISSIONS.VIEW_TIERS.UPDATE)) {
      toast.error('You are not authorized to perform this action.');
      return;
    }

    const { isValid, errors } = validateEditForm(state.editForm);

    if (!isValid) {
      setState({ fieldErrors: errors });
      return;
    }

    const { editForm, drawerData } = state;
    if (!drawerData) return;

    setState({ editSaving: true });

    try {
      const payload = {
        id: drawerData.id,
        name: editForm.name.trim(),
        minPoint: parseFloat(editForm.minPoints),
        redemptionRatio: parseFloat(editForm.redemptionRatio),
        description: editForm.description ? editForm.description.trim() : '',
      };

      await updateTier(token, payload);

      const updatedTier = {
        ...drawerData,
        name: payload.name,
        minPoint: payload.minPoint,
        redemptionRatio: payload.redemptionRatio,
        description: payload.description,
      };

      const updatedList = state.tiersData.map((t) =>
        t.id === drawerData.id ? updatedTier : t
      );

      setState({
        drawerData: updatedTier,
        editMode: false,
        editSaving: false,
        tiersData: updatedList,
      });

      toast.success('Tier updated successfully');
    } catch (err) {
      console.error('Update error', err);
      setState({ editSaving: false });
      toast.error('Failed to update tier. Please try again.');
    }
  };

  const handleCloseDrawer = () => {
    setState({ showTierDrawer: false, drawerData: null, editMode: false });
  };

  const handleDeleteTier = (tierData) => {
    setState({ showDeleteConfirmation: true, tierToDelete: tierData });
  };

  const handleConfirmDelete = async () => {
    const tierData = state.tierToDelete;
    if (!tierData) return;

    if (!can(TIER_MANAGEMENT_PERMISSIONS.VIEW_TIERS.DELETE)) {
      toast.error('You are not authorized to perform this action.');
      setState({ showDeleteConfirmation: false, tierToDelete: null });
      return;
    }

    try {
      setState({ loading: true, showDeleteConfirmation: false });

      await deleteTier(token, tierData.id);

      const updatedList = state.tiersData.filter((t) => t.id !== tierData.id);

      setState({
        tiersData: updatedList,
        showTierDrawer: false,
        drawerData: null,
        editMode: false,
        loading: false,
        tierToDelete: null,
      });

      toast.success('Tier deleted successfully');
    } catch (err) {
      console.error('Delete error', err);
      setState({ loading: false, tierToDelete: null });
      toast.error('Failed to delete tier. Please try again.');
    }
  };

  const handleCancelDelete = () => {
    setState({ showDeleteConfirmation: false, tierToDelete: null });
  };

  const renderDrawer = () => {
    const data = state.drawerData;
    if (!data) return null;

    return (
      <>
        <Drawer.Header>
          <div className={styles.drawer_header}>
            <Drawer.Title>{!state.editMode ? 'Tier Details' : 'Edit Tier Details'}</Drawer.Title>
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.drawer_body}>
            {!state.editMode ? (
              <>
                <div className={styles.detail_row}>
                  <strong>Tier Name:</strong>
                  <span>{data.name || 'N/A'}</span>
                </div>
                <div className={styles.detail_row}>
                  <strong>Min Redemption Points:</strong>
                  <span>{data.minPoint || 'N/A'}</span>
                </div>
                <div className={styles.detail_row}>
                  <strong>Redemption Ratio:</strong>
                  <span>{data.redemptionRatio !== undefined ? data.redemptionRatio : 'N/A'}</span>
                </div>
                <div className={styles.detail_row}>
                  <strong>Description:</strong>
                  <span>{data.description || 'N/A'}</span>
                </div>
              </>
            ) : (
              <div className={styles.edit_form}>
                <label className={styles.form_label}>
                  Tier Name *
                  <input
                    type="text"
                    value={state.editForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g. Bronze, Silver, Gold"
                    autoFocus
                    className={state.fieldErrors?.name ? styles.input_error : ''}
                  />
                  {state.fieldErrors?.name && (
                    <span className={styles.error_message}>{state.fieldErrors.name}</span>
                  )}
                </label>

                <label className={styles.form_label}>
                  Minimum Redemption Points *
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={state.editForm.minPoints}
                    onChange={(e) => handleInputChange('minPoints', e.target.value)}
                    placeholder="e.g. 1"
                    className={state.fieldErrors?.minPoints ? styles.input_error : ''}
                  />
                  {state.fieldErrors?.minPoints && (
                    <span className={styles.error_message}>{state.fieldErrors.minPoints}</span>
                  )}
                </label>

                <label className={styles.form_label}>
                  Redemption Ratio *
                  <input
                    type="number"
                    min="0.01"
                    step="any"
                    value={state.editForm.redemptionRatio}
                    onChange={(e) => handleInputChange('redemptionRatio', e.target.value)}
                    placeholder="e.g. 1.0"
                    className={state.fieldErrors?.redemptionRatio ? styles.input_error : ''}
                  />
                  {state.fieldErrors?.redemptionRatio && (
                    <span className={styles.error_message}>{state.fieldErrors.redemptionRatio}</span>
                  )}
                </label>

                <label className={styles.form_label}>
                  Description
                  <input
                    type="text"
                    value={state.editForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description"
                  />
                </label>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', margin: '0 20px', gap: '10px' }}>
            {!state.editMode ? (
              <>
                <PermissionGate permission={TIER_MANAGEMENT_PERMISSIONS.VIEW_TIERS.UPDATE}>
                  <button
                    type="button"
                    className={`btn btn-primary ${styles.edit_button}`}
                    onClick={() => setState({ editMode: true })}
                  >
                    Edit
                  </button>
                </PermissionGate>
                <PermissionGate permission={TIER_MANAGEMENT_PERMISSIONS.VIEW_TIERS.DELETE}>
                  <button
                    type="button"
                    className={`btn btn-danger ${styles.edit_button}`}
                    onClick={() => handleDeleteTier(state.drawerData)}
                  >
                    Delete
                  </button>
                </PermissionGate>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className={`btn btn-secondary ${styles.edit_button}`}
                  onClick={() => setState({ editMode: false, fieldErrors: {} })}
                  disabled={state.editSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-primary ${styles.edit_button}`}
                  onClick={handleSaveTier}
                  disabled={state.editSaving}
                >
                  {state.editSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </Drawer.Body>
      </>
    );
  };

  return (
    <div className={styles.root_div}>
      {loading ? (
        <Loading text="Loading" />
      ) : (
        <div>
          <PageHeader text="Manage Tiers" />
          {tiersLoadingError ? (
            <p>{tiersLoadingError}</p>
          ) : (
            <div>
              <div className={styles.filter_btn_group}></div>
              <DataTable
                tableData={tiersData}
                tableheading={tableHeading}
                handleOnClick={handleDrawerOpen}
                tag="tier details"
                fetchError=""
                searchFilterLoading={false}
              />
            </div>
          )}
        </div>
      )}

      <CancelAlertPopup
        show={state.showDeleteConfirmation}
        alertMessage={`Are you sure you want to delete the tier "${state.tierToDelete?.name}"? This action cannot be undone.`}
        handleYesBtnClick={handleConfirmDelete}
        handleNoBtnClick={handleCancelDelete}
      />

      <Drawer
        show={state.showTierDrawer}
        onHide={handleCloseDrawer}
        placement="right"
      >
        {renderDrawer()}
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(TiersManagement);
